import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter, HostBinding,
  Inject, INJECTOR, Injector,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from "@angular/forms";
import {BehaviorSubject,} from "rxjs";
import {CHECK_LAYOUT_DIRECTION_PROVIDER} from "../../../providers";
import {IntLabeledFieldBoxComponent} from "../../boxes";
import {IntDefaultInputField} from "./int-default-input-field.component";
import {DestroyStreamService} from "../../../services";
import {distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {TVerticalAlign} from "../../../types";

@Component({
  selector :'int-default-input',
  templateUrl: './int-default-input.component.html',
  styleUrls: ['./int-default-input-vars.scss', './int-default-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntDefaultInputComponent,
      multi: true
    },
    DestroyStreamService
  ]
})
export class IntDefaultInputComponent implements ControlValueAccessor, OnInit, AfterViewInit {

  value$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  field$ = new BehaviorSubject<ElementRef>({} as ElementRef);

  dir$ = new BehaviorSubject('ltr');

  control!: NgControl;

  @Input() useTrim = false;

  @Input()
  set value(value) {
    this.value$.next(value);
  }

  @Input()
  set direction(value: string) {
    if(value) {
      this.dir$.next(value);
    }else{
      this.dir$.next(this.directionValue.value)
    }
  }

  get value(): string {
    return this.value$.value;
  }

  @Input()
  name: string | null = null;

  @Input()
  placeholder = '';

  @Input()
  label = '';

  @Input()
  type = 'text';

  @Input() defaultHeight = '50px';
  @Input() defaultHeightPriority = false;

  @Input()
  showLabelTip = true;

  @Input()
  readonly = false;

  @Input()
  autocomplete: string | null = null;

  @Input()
  hideField: boolean = false;

  @Input()
  verticalAlign: TVerticalAlign = 'center';

  @Output()
  changeValueEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  onBlurEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  onFocusEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  disabled = false;
  touched = false;
  focused = false;

  @ViewChild('rootElement', { static: false })
  private readonly rootElement!: ElementRef;

  @ViewChild('field', { static: false })
  private readonly field!: ElementRef;

  @ViewChild('colswrap', { static: true })
  private readonly colswrap!: ElementRef;

  @ViewChild(IntLabeledFieldBoxComponent) private readonly box!: IntLabeledFieldBoxComponent;

  @ViewChild(IntDefaultInputField) dField!: IntDefaultInputField;

  @ContentChild(IntDefaultInputField) _field!: IntDefaultInputField;

  hideDefaultField = false;

  isError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(INJECTOR) private injector: Injector,
    private readonly renderer2: Renderer2,
    @Inject(CHECK_LAYOUT_DIRECTION_PROVIDER)
    private readonly directionValue: BehaviorSubject<string>,
    private readonly destroy$: DestroyStreamService,
    private readonly cdr: ChangeDetectorRef
  ) {

  }

  @HostBinding('class._disabled')
  get _disabled() {
    return this.disabled;
  }

  ngAfterViewInit() {

    if(!this._field) {
      this._field = this.dField;
    }else{
      this.hideDefaultField = true;
    }

    this.cdr.detectChanges();

    this._field.onFocus
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.focused = true;
        this.markAsTouched();
        this.focusView();
      });

    this._field.onBlur
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.focused = false;
        this.onBlurEvent.emit(true)
        this.box.setBlur();
        this.onTouched();
        this.cdr.markForCheck();
      });

    this._field.onInput
      .pipe(
        tap((value) => this.changeValueEvent.emit(value)),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        this.setValue(value);
      });

    this.value$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this._field.value = data;
        this.cdr.detectChanges();
      });

    this.control?.statusChanges
      ?.pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )?.subscribe(status => {
        this.isError$.next(status === 'INVALID');
        this.cdr.markForCheck();
      })
  }

  ngOnInit() {
    this.dir$.next(this.directionValue.value);
    this.setControl();
  }

  get isDirty() {
    if(this.control) {
      return Boolean(this.control?.dirty)
    }
    return false;
  }

  get isError(): boolean {
    if(this.control) {
      return this.isError$.value && Boolean(this.control?.dirty)
    }
    return false;
  }

  get currentLabel(): string {
    return this.label || this.placeholder || ''
  }

  get isNotLabelOrPlaceholder(): boolean {
    return !(Boolean(this.label) || Boolean(this.placeholder));
  }

  get isFocused() {
    return this.focused;
  }

  get isFill() {
    return this.value$.value?.length > 0
  }

  get hidePlaceholder() {
    return !this.label && this.isFocused;
  }

  private getPreparedValue(value: string | null | undefined) {
    const _v = (Boolean(value) && value !== null && value !== undefined) ? value.toString() : '';
    return this.useTrim ? _v.trim() : _v;
  }

  private setValue(value: string) {
    const _value = this.getPreparedValue(value);
    this.onChange(_value);
    this.value$.next(_value);
  }

  setControl() {
    try {
      this.control = this.injector.get(NgControl);
    }catch (err) {
      console.log('please provide NgControl for [IntDefaultInputComponent]')
    }
  }

  writeValue(value: string) {
    const _value = this.getPreparedValue(value);
    this.value$.next(_value);
    if(this._field) {
      this._field.value = _value;
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    if(this._field) {
      this._field.disabled = disabled;
    }
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  changeValue(event: Event) {}

  onChange(val: any) {}

  onTouched() {}

  private focusView() {
    this.onFocusEvent.emit(true)
    this.box.setFocus();
    this.cdr.detectChanges();
  }

  setBlur() {
    this._field?.elementRef.nativeElement.blur();
  }

  setFocus() {
    if(!this.disabled) {
      this._field?.elementRef.nativeElement.focus();
      this.cdr.detectChanges();
    }
  }

  setPseudoFocus() {
    this.box.setPseudoFocus();
    this.focusView();
  }

  onFocus() {
    this.focused = true;
    this.onFocusEvent.emit(true);
    this.onBlurEvent.emit(false);
  }
  onBlur() {
    this.focused = false;
    this.onFocusEvent.emit(false);
    this.onBlurEvent.emit(true);
  }
}
